
import VueApp from '@/@types/app/VueApp';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { TextTab } from '@/models/article/TextTab';
import githubIcon from '@/assets/img/integrations/github.svg';
import bitbucketIcon from '@/assets/img/integrations/bitbucket.svg';
import confluenceIcon from '@/assets/img/integrations/confluence.svg';
import gdocsIcon from '@/assets/img/integrations/gdocs.svg';

const SOURCE_ICONS: Record<string, string> = {
  GITHUB: githubIcon,
  BITBUCKET: bitbucketIcon,
  CONFLUENCE: confluenceIcon,
  GDRIVE: gdocsIcon,
};
import articleEditService from '@/services/article/ArticleEditService';
import { commitSetArticleEditorTextTabs } from '@/store/commits/articleEditorCommits';
import confirmationService from '@/services/ConfirmationService';
import { DeletedItemStyleClass } from '@/@types/enums/ConfirmationDeleteItem/DeletedItemStyleClass';
import { ElLoadingComponent } from 'element-ui/types/loading';
import { commitSetArticleMode } from '@/store/commits/sharedCommits';
import { ArticleEditMode } from '@/@types/ArticleEditMode';

@Component
export default class ArticleTextTabButton extends VueApp {
  loader: ElLoadingComponent = null;

  $refs!: {
    renamingInput: any;
  };

  @Prop({ type: Boolean, default: false }) isActive: boolean;
  @Prop({ type: Boolean, default: false }) isAllowPin: boolean;
  @Prop({ type: Boolean, default: false }) isPinned: boolean;
  @Prop({ type: Boolean, default: false }) isEditMode: boolean;
  @Prop({ type: Boolean, default: false }) isReordering: boolean;
  @Prop({ type: TextTab, required: true }) textTab: TextTab;
  @Prop(String) title: string;
  @Prop({ type: String, default: null }) sourceType: string | null;

  isRenaming = false;
  changedTitle = '';

  get articleEditMode() {
    return this.$store.state.articleMode;
  }

  get computedActive() {
    return this.isActive;
  }

  get isExternalArticle(): boolean {
    return !!this.sourceType && this.sourceType !== 'SNAPSHOT';
  }

  get sourceIconUrl(): string | null {
    return this.sourceType ? SOURCE_ICONS[this.sourceType.toUpperCase()] ?? null : null;
  }

  get trimmedTitle() {
    const charLimit = this.isPinned ? 10 : 13;
    if (this.title.length > charLimit) {
      return this.title.slice(0, charLimit) + '...';
    }

    return this.title;
  }

  handleClick() {
    this.$emit('click');
  }

  @Watch('isRenaming')
  onRenamingChange(isRenaming: boolean) {
    if (isRenaming) {
      this.$nextTick(() => {
        if (this.$refs.renamingInput) {
          const input = this.$refs.renamingInput.$el as HTMLInputElement;
          input.focus();
          const length = input.value.length;
          input.setSelectionRange(length, length);
        }
      });
    }
  }

  async handleClickSaveRename() {
    this.loader = this.$loading({
      lock: true,
      text: 'Renaming note',
    });
    const currentTabs = this.$store.state.articleEditor.article.textTabs;

    if (!this.changedTitle.length) this.changedTitle = 'Note';

    const tab = currentTabs.find((item) => item.id === this.textTab.id);

    if (tab) tab.title = this.changedTitle;

    try {
      await articleEditService.saveAsDraft();
    } finally {
      this.loader.close();
      this.loader = null;
      this.isRenaming = false;
    }
  }

  handleStartRenaming() {
    this.changedTitle = this.textTab.title;
    this.isRenaming = true;
  }

  handleCancelRenaming() {
    this.isRenaming = false;
  }

  handleClickPin() {
    if (!this.isAllowPin) return;
    this.$emit('pin', !this.isPinned);
  }

  async handleClickDelete() {
    const result = await confirmationService
      .showRemovePopup(this.textTab.title, '', DeletedItemStyleClass.ARTICLE)
      .catch(() => 'cancel');

    if (result !== 'confirm') return;

    this.loader = this.$loading({
      lock: true,
      text: 'Deleting note',
    });

    const textTabs = articleEditService.articleEditor.article.textTabs;
    const updatedTextTabsList = textTabs.filter((item) => item.id !== this.textTab.id);
    commitSetArticleEditorTextTabs(updatedTextTabsList);

    if (!updatedTextTabsList.length) {
      commitSetArticleMode(ArticleEditMode.VIDEO);
    } else {
      commitSetArticleMode(ArticleEditMode.NOTES);
      articleEditService.setActiveTextTab({
        article: articleEditService.articleEditor.article,
        textTabId: updatedTextTabsList[0].id,
      });
    }

    try {
      await articleEditService.saveAsDraft();
    } finally {
      this.loader.close();
      this.loader = null;
    }
  }
}
